import { Show, createMemo, type Component } from "solid-js";
import { A } from "@solidjs/router";
import type { OrderStore } from "~/routes/(layout)/order/(order)";
import { useItemOrderContext } from "~/utils/contexts";
import { calcOrderQtyInFeet } from "./utils";
import { stockThreshold, ThresholdCheckout } from "~/utils/threshold";
import { StockSubscribeButton } from "~/components/stock-subscription";
import { Icon } from "solid-heroicons";
import {
  bellAlert,
  shoppingCart,
  arrowsPointingOut,
  exclamationCircle,
} from "solid-heroicons/outline";
import { PT } from "~/utils/products";

type NoAvailableInventoryProps = {
  orderData: OrderStore;
  clearFn?: () => void;
};

export const NoAvailableInventory: Component<NoAvailableInventoryProps> = (
  props,
) => {
  const { orderData } = useItemOrderContext();

  const orderQtyInFeet = createMemo(() =>
    calcOrderQtyInFeet({
      productType: orderData.selected.productType as PT,
      shipsFromStockLevel: orderData.selected.shipsFromStockLevel,
      quantity: orderData.selected.quantity,
      linearFt: orderData.selected.linearFt,
      width: orderData.selected.width,
      height: orderData.selected.height,
      railLength: orderData.selected.railLength,
      sizing: orderData.selected.sizing,
      mouldingWidth: orderData.width ?? 0,
      boxFeet:
        orderData.selected.productType === PT.BOX
          ? (orderData?.pricing?.Pricing?.box?.Quantity ?? 0)
          : 0,
    }),
  );

  const checkout = createMemo(() => {
    return stockThreshold(
      (orderData?.selected?.shipsFromStockLevel ?? 0) - orderQtyInFeet(),
      // 0,
      orderData?.currentProduct?.Category,
      orderData?.currentProduct?.Discontinued,
    ).checkout;
  });

  return (
    <>
      <Show when={checkout() === ThresholdCheckout.blockAndSubscribe}>
        <div class="bg-roma-grey/50 rounded-xlzzzz shadow-sm border border-gray-300 p-6 flex flex-col gap-2">
          <div class="flex items-center gap-3  pb-4 mb-2 border-b">
            <div>
              <Icon path={exclamationCircle} class="w-6 h-6 text-red-600" />
            </div>
            <h3 class="grow text-lg font-bold ">Out of Stock</h3>
          </div>

          <p>
            Oops — this item is temporarily unavailable. At this time, we’re not
            accepting backorders. Select Notify Me to receive a restock alert,
            or choose an alternative item to proceed.
          </p>
          <ul class="text-small  text-roma-blue mt-3 flex flex-col gap-2">
            <li>
              <StockSubscribeButton
                product={orderData.currentProduct}
                inventory={orderData.pricing}
                onCloseAfterSubscribe={props.clearFn}
                trigger={({ open, isSubscribed }) => (
                  <button class="flex items-center gap-1.5" onClick={open}>
                    <Icon path={bellAlert} class="w-5 h-5 " />
                    <span>Notify Me</span>
                  </button>
                )}
              />
            </li>
            <li>
              <A class="flex items-center gap-1.5" href="/shop">
                <Icon path={shoppingCart} class="w-5 h-5 " />
                <span>Explore our Shop</span>
              </A>
            </li>
            <li>
              <button class="flex items-center gap-1.5" onClick={props.clearFn}>
                <Icon path={arrowsPointingOut} class="w-5 h-5 " />
                <span>Select Another Moulding</span>
              </button>
            </li>
          </ul>
        </div>
      </Show>
    </>
  );
};
